import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FilterListIcon from '@mui/icons-material/FilterList';

import MultiSelect from '~/components/General/MultiSelectNew';

import Tooltip from '../ScrollDismissTooltip';

import {
  anyFilterOption,
  formatCurrencyFilterInput,
  getFilterDisplayLabel,
  getFilterDropdownOptions,
  getFilterOptionLabel,
  getFilterPlaceholderText,
  isCurrencyFilter,
  isMultiOptionFilter,
  parseCurrencyFilterInput,
  maxVisibleMultiFilterOptions,
} from '../utils/filters';

const FiltersPanel = ({
  clearFilterValue,
  contactTypeCopy,
  filterDefinitions,
  filterDraftCount,
  filterDraftPayload,
  hasPendingFilterDraftChanges,
  invalidFilterDraftKeys,
  isFilterDraftInvalid,
  lookupLoadingByKey = {},
  lookupOptionLabelsByKey = {},
  lookupOptionsByKey = {},
  normalizedFilterDraft,
  onLookupSearch = () => {},
  onApplyFilters,
  refinePanelRef,
  refineRowRef,
  selectedFilterItems,
  selectedFilterTooltipByKey,
  selectFilter,
  setFilterBooleanValue,
  setFilterMultiValues,
  setFilterRangeValue,
  setFilterSingleValue,
  showFilters,
  toggleFiltersPanel,
}) => (
  <>
    <Box className="refineRow" ref={refineRowRef}>
      <button
        className="refineButton"
        onClick={toggleFiltersPanel}
        type="button"
      >
        <FilterListIcon />
        Filter list
      </button>
      {selectedFilterItems.map((filter) => (
        <Tooltip
          arrow
          disableInteractive
          key={filter.key}
          placement="top"
          title={selectedFilterTooltipByKey[filter.key] || filter.label}
        >
          <Chip
            label={filter.label}
            className="selectedFilter refineChip"
            onClick={selectFilter}
            onDelete={() => clearFilterValue(filter.key, true)}
          />
        </Tooltip>
      ))}
    </Box>

    {showFilters && (
      <Box className="inlineFiltersPanel" ref={refinePanelRef}>
        <Box className="inlineFiltersHeader">
          <Box>
            <Typography className="inlineFiltersTitle">List filters</Typography>
            <Typography className="inlineFiltersSubtitle">
              Add filters to target the {contactTypeCopy.plural} you want, then apply them to refresh the list.
            </Typography>
          </Box>
          <button
            className="inlineFiltersApplyButton"
            disabled={isFilterDraftInvalid || !hasPendingFilterDraftChanges}
            onClick={onApplyFilters}
            type="button"
          >
            Apply {filterDraftCount || 0} filter{filterDraftCount === 1 ? '' : 's'}
          </button>
        </Box>

        <Box className="inlineFilterRows">
          {filterDefinitions.map((definition, index) => {
            const draftValue = normalizedFilterDraft[definition.key];
            const hasFilledFilter = Boolean(filterDraftPayload[definition.key]);
            const displayLabel = getFilterDisplayLabel(definition);
            const isInvalidRange = invalidFilterDraftKeys.has(definition.key);
            const shouldShowCategory = definition.category !== filterDefinitions[index - 1]?.category;
            const isAsyncLookup = Boolean(definition.asyncLookup);
            const isCurrencyRange = isCurrencyFilter(definition);
            const lookupOptions = lookupOptionsByKey?.[definition.key] || [];
            const filterLabelId = `find-new-contacts-filter-${definition.key}-label`;
            const getRangeInputValue = (value) =>
              isCurrencyRange ? formatCurrencyFilterInput(value) : value;
            const getRangeChangeValue = (value) =>
              isCurrencyRange ? parseCurrencyFilterInput(value) : value;
            const getDisplayOptionLabel = (value) =>
              lookupOptionLabelsByKey?.[definition.key]?.[value] || getFilterOptionLabel(definition, value);

            return (
              <React.Fragment key={definition.key}>
                {shouldShowCategory && (
                  <Box className="inlineFilterCategory">
                    {definition.category}
                  </Box>
                )}
                <Box className={hasFilledFilter ? 'inlineFilterRow applied' : 'inlineFilterRow'}>
                  <Box className="inlineFilterSummary">
                    <span>
                      <strong id={filterLabelId}>{displayLabel}</strong>
                      <small>{definition.helper}</small>
                    </span>
                    {hasFilledFilter && (
                      <button
                        className="filterClearButton"
                        onClick={() => clearFilterValue(definition.key)}
                        type="button"
                      >
                        Clear
                      </button>
                    )}
                  </Box>

                  <Box className="inlineFilterControls">
                    {definition.type === 'boolean' && (
                      <label className="filterBooleanControl">
                        <input
                          checked={Boolean(draftValue?.enabled)}
                          onChange={(event) => setFilterBooleanValue(definition.key, event.target.checked)}
                          type="checkbox"
                        />
                        <span>Use this filter</span>
                      </label>
                    )}

                    {definition.type === 'range' && (
                      <>
                        <Box className="filterRangeInputs">
                          <TextField
                            error={isInvalidRange}
                            inputProps={{ inputMode: isCurrencyRange ? 'decimal' : undefined, max: definition.max, min: definition.min, step: definition.step }}
                            onChange={(event) => setFilterRangeValue(definition.key, 'min', getRangeChangeValue(event.target.value))}
                            placeholder="Min"
                            size="small"
                            type={isCurrencyRange ? 'text' : 'number'}
                            value={getRangeInputValue(draftValue?.min ?? '')}
                          />
                          <TextField
                            error={isInvalidRange}
                            inputProps={{ inputMode: isCurrencyRange ? 'decimal' : undefined, max: definition.max, min: definition.min, step: definition.step }}
                            onChange={(event) => setFilterRangeValue(definition.key, 'max', getRangeChangeValue(event.target.value))}
                            placeholder="Max"
                            size="small"
                            type={isCurrencyRange ? 'text' : 'number'}
                            value={getRangeInputValue(draftValue?.max ?? '')}
                          />
                        </Box>
                        {isInvalidRange && (
                          <Typography className="filterRangeError">
                            Max must be greater than or equal to min.
                          </Typography>
                        )}
                      </>
                    )}

                    {definition.type === 'single' && (
                      <TextField
                        className="filterSingleSelect"
                        onChange={(event) => setFilterSingleValue(definition.key, event.target.value)}
                        select
                        SelectProps={{
                          displayEmpty: true,
                          labelId: filterLabelId,
                          MenuProps: {
                            classes: {
                              paper: 'filterSingleSelectMenu',
                            },
                            MenuListProps: {
                              className: 'filterSingleSelectMenuList',
                            },
                          },
                          renderValue: (selected) => (
                            selected
                              ? getFilterOptionLabel(definition, selected)
                              : <span className="filterSingleSelectPlaceholder">{getFilterPlaceholderText(definition)}</span>
                          ),
                        }}
                        size="small"
                        value={draftValue?.value || ''}
                      >
                        <MenuItem
                          className="filterSingleSelectOption"
                          value=""
                        >
                          {anyFilterOption}
                        </MenuItem>
                        {getFilterDropdownOptions(definition).map((option) => (
                          <MenuItem
                            className="filterSingleSelectOption"
                            key={option.id}
                            value={option.id}
                          >
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                    {isMultiOptionFilter(definition) && (
                      <MultiSelect
                        activeState={hasFilledFilter}
                        className={hasFilledFilter ? 'findContactsFilterDropdown activeState' : 'findContactsFilterDropdown'}
                        clearable={false}
                        component="multipleIds"
                        getOptionLabel={getDisplayOptionLabel}
                        inlineMenu
                        noOptionText="No options found"
                        onSearchChange={isAsyncLookup ? (search, options) => onLookupSearch(definition.key, search, options) : undefined}
                        options={isAsyncLookup ? lookupOptions : getFilterDropdownOptions(definition)}
                        placeholderOptionText={anyFilterOption}
                        placeHolderText={hasFilledFilter ? displayLabel : anyFilterOption}
                        searchFieldPlaceholderText={displayLabel}
                        searchLoading={isAsyncLookup ? Boolean(lookupLoadingByKey?.[definition.key]) : undefined}
                        searchOnOpen={isAsyncLookup}
                        maxRenderedOptions={definition.maxRenderedOptions}
                        selectedPreviewLimit={maxVisibleMultiFilterOptions}
                        selectedValue={draftValue?.values || []}
                        setSelectedValue={(values) => setFilterMultiValues(definition.key, values)}
                      />
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    )}
  </>
);

export default FiltersPanel;
